import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { HelpCircle } from 'lucide-react'
import styles from './HelpTip.module.css'

/**
 * HelpTip renders a small info icon next to a label. Hovering or focusing the
 * icon surfaces a tooltip bubble with `text`. The bubble is dismissed on blur,
 * mouse-leave, or Escape.
 *
 * Falls back to the native `title` attribute so assistive tech (and keyboard
 * users who don't focus the icon) still get the content.
 *
 * Props:
 *  - text      : string the bubble displays (required)
 *  - label     : a11y label for the trigger button (default "More info")
 *  - placement : 'top' | 'bottom' | 'right' (default 'top')
 *  - size      : pixel size for the icon (default 14)
 */
export function HelpTip({ text, label = 'More info', placement = 'top', size = 14 }) {
  const [open, setOpen] = useState(false)
  const tipId = useId()
  const buttonRef = useRef(null)

  const show = useCallback(() => setOpen(true), [])
  const hide = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return undefined
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.blur()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open])

  return (
    <span className={styles.wrap}>
      <button
        type="button"
        ref={buttonRef}
        className={styles.trigger}
        aria-label={label}
        aria-describedby={open ? tipId : undefined}
        title={text}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <HelpCircle size={size} aria-hidden="true" />
      </button>
      {open ? (
        <span
          id={tipId}
          role="tooltip"
          className={`${styles.bubble} ${styles[`bubble_${placement}`] || ''}`}
        >
          {text}
        </span>
      ) : null}
    </span>
  )
}

export default HelpTip
